/**
 * Playlist-Based Recommendations Component
 */
import React, { useState, useEffect } from 'react';
import { authenticatedFetch } from '@/lib/auth';
import { useMoodEngine, MOOD_CONFIGS } from '@/hooks/useMoodEngine';

interface Playlist {
  id: string;
  name: string;
  description: string;
  track_count: number;
  image: string | null;
  owner: string;
  public: boolean;
}

interface MatchedArtist {
  spotify_name: string;
  matched_name: string;
  spotify_id: string;
}

interface UnmatchedArtist {
  spotify_name: string;
  spotify_id: string;
}

interface ArtistMatchResult {
  matched: MatchedArtist[];
  unmatched: UnmatchedArtist[];
  total: number;
  matched_count: number;
  match_rate: number;
}

interface Recommendation {
  artist: string;
  score: number;
  breakdown: {
    relevance: number;
    diversity: number;
    declustering: number;
  };
}

interface EnrichedArtist {
  artist_name: string;
  found: boolean;
  spotify_artist?: {
    id: string;
    name: string;
    image: string | null;
  };
  top_track?: {
    id: string;
    name: string;
    uri: string;
    preview_url: string | null;
    album: string;
    album_image: string | null;
  } | null;
}

const PlaylistRecommender: React.FC = () => {
  const { selectedMood } = useMoodEngine();

  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [loadingPlaylists, setLoadingPlaylists] = useState(false);
  const [selectedPlaylist, setSelectedPlaylist] = useState<Playlist | null>(null);
  const [artistMatch, setArtistMatch] = useState<ArtistMatchResult | null>(null);
  const [loadingArtists, setLoadingArtists] = useState(false);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [enrichedArtists, setEnrichedArtists] = useState<EnrichedArtist[]>([]);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [recSource, setRecSource] = useState<string | null>(null);
  const [loadingEnrich, setLoadingEnrich] = useState(false);
  const [loadingPlayAll, setLoadingPlayAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updateKey, setUpdateKey] = useState(0); // Force re-render

  useEffect(() => {
    fetchPlaylists();
  }, []);

  const fetchPlaylists = async () => {
    setLoadingPlaylists(true);
    setError(null);
    try {
      const response = await authenticatedFetch('/api/auralist/playlists?limit=50');
      if (!response.ok) throw new Error('Failed to fetch playlists');
      const data = await response.json();
      setPlaylists(data.playlists || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load playlists');
    } finally {
      setLoadingPlaylists(false);
    }
  };

  const selectPlaylist = async (playlist: Playlist) => {
    setSelectedPlaylist(playlist);
    setArtistMatch(null);
    setRecommendations([]);
    setEnrichedArtists([]);
    setLoadingArtists(true);
    setError(null);
    try {
      const response = await authenticatedFetch(`/api/auralist/playlist/${playlist.id}/artists`);
      if (!response.ok) throw new Error('Failed to fetch playlist artists');
      const data = await response.json();
      setArtistMatch(data.artists);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load artists');
    } finally {
      setLoadingArtists(false);
    }
  };

  const handleGetRecommendations = async () => {
    if (!selectedPlaylist || !selectedMood) {
      setError('Please select a mood and playlist first');
      return;
    }

    // Clear and show loading
    setLoadingRecs(true);
    setRecommendations([]);
    setEnrichedArtists([]);
    setRecSource(null);
    setError(null);

    try {
      const response = await authenticatedFetch('/api/auralist/recommendations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          playlist_id: selectedPlaylist.id,
          mood: selectedMood,
          limit: 20,
        }),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      const recs = data.recommendations || [];

      // Debug: Show result count
      if (recs.length === 0) {
        setError(`No recommendations found. API returned: ${JSON.stringify(data).slice(0, 100)}`);
      }

      // Set source for display
      setRecSource(data.source || 'music-recs-api');

      // Force update
      setRecommendations(recs);
      setUpdateKey(prev => prev + 1);

      // Enrich in background
      if (recs.length > 0) {
        enrichArtists(recs.map((r: Recommendation) => r.artist));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate recommendations');
    } finally {
      setLoadingRecs(false);
    }
  };

  const enrichArtists = async (artistNames: string[]) => {
    setLoadingEnrich(true);
    try {
      const response = await authenticatedFetch('/api/auralist/enrich', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(artistNames),
      });
      if (response.ok) {
        const data = await response.json();
        setEnrichedArtists(data.enriched || []);
      }
    } catch (err) {
      // Enrichment is optional, don't show error
    } finally {
      setLoadingEnrich(false);
    }
  };

  const playTrack = async (uri: string) => {
    try {
      await authenticatedFetch('/api/spotify/player/play', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uris: [uri] }),
      });
    } catch (err) {
      setError('Failed to play. Make sure Spotify is active.');
    }
  };

  const playAll = async () => {
    if (recommendations.length === 0) return;
    setLoadingPlayAll(true);
    setError(null);
    try {
      const response = await authenticatedFetch('/api/auralist/play-all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ artist_names: recommendations.map(r => r.artist) }),
      });
      if (!response.ok) throw new Error('Failed to play all');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to play all');
    } finally {
      setLoadingPlayAll(false);
    }
  };

  const getEnrichedArtist = (artistName: string): EnrichedArtist | undefined => {
    return enrichedArtists.find(e => e.artist_name.toLowerCase() === artistName.toLowerCase());
  };

  return (
    <div className="card">
      <h3 className="text-xl font-bold mb-4">2. Pick a playlist</h3>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-300 mb-2">Select a Playlist</label>
        {loadingPlaylists ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-spotify-green"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-64 md:max-h-48 overflow-y-auto scrollbar-hidden">
            {playlists.map((playlist) => (
              <button
                key={playlist.id}
                onClick={() => selectPlaylist(playlist)}
                className={`min-h-touch p-3 md:p-2 rounded-lg text-left transition-all active:scale-[0.98] ${
                  selectedPlaylist?.id === playlist.id
                    ? 'bg-spotify-green/30 border-2 border-spotify-green'
                    : 'bg-gray-800/50 border-2 border-transparent hover:bg-gray-700/50'
                }`}
              >
                <div className="flex items-center gap-3 md:gap-2">
                  {playlist.image ? (
                    <img src={playlist.image} alt={playlist.name} className="w-12 h-12 md:w-10 md:h-10 rounded object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-12 h-12 md:w-10 md:h-10 rounded bg-gray-700 flex items-center justify-center text-gray-400 flex-shrink-0">
                      <span className="text-xl md:text-lg">&#9836;</span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm md:text-xs font-medium text-white truncate">{playlist.name}</p>
                    <p className="text-xs md:text-[10px] text-gray-400">{playlist.track_count} tracks</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedPlaylist && (
        <div className="mb-6 p-4 bg-gray-800/50 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-white">Artists in "{selectedPlaylist.name}"</h4>
            {artistMatch && (
              <span className={`text-xs px-2 py-1 rounded-full ${
                artistMatch.match_rate > 70 ? 'bg-green-500/20 text-green-400' :
                artistMatch.match_rate > 40 ? 'bg-yellow-500/20 text-yellow-400' :
                'bg-red-500/20 text-red-400'
              }`}>
                {artistMatch.matched_count}/{artistMatch.total} matched ({artistMatch.match_rate}%)
              </span>
            )}
          </div>
          {loadingArtists ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-spotify-green"></div>
            </div>
          ) : artistMatch ? (
            <div className="flex flex-wrap gap-1">
              {artistMatch.matched.slice(0, 15).map((artist) => (
                <span key={artist.spotify_id} className="text-[10px] px-2 py-0.5 rounded-full bg-spotify-green/20 text-spotify-green">
                  {artist.spotify_name}
                </span>
              ))}
              {artistMatch.matched.length > 15 && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-700 text-gray-400">
                  +{artistMatch.matched.length - 15} more
                </span>
              )}
            </div>
          ) : null}
        </div>
      )}

      {selectedPlaylist && artistMatch && artistMatch.matched_count > 0 && (
        <div className="mb-6">
          {selectedMood && (
            <div className="mb-4 p-3 bg-spotify-green/10 border border-spotify-green/30 rounded-lg">
              <p className="text-sm text-gray-300">
                Finding <span className="text-spotify-green font-semibold">
                  {MOOD_CONFIGS.find(m => m.mood === selectedMood)?.label}
                </span> music
              </p>
            </div>
          )}
          <button
            onClick={handleGetRecommendations}
            disabled={loadingRecs || !selectedMood}
            className="w-full px-4 py-3 bg-spotify-green text-black font-semibold rounded-lg hover:bg-spotify-green/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loadingRecs ? (
              <span className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
                Finding tracks...
              </span>
            ) : !selectedMood ? (
              'Select a mood first'
            ) : (
              'Get 20 Recommendations'
            )}
          </button>
        </div>
      )}

      {recommendations.length > 0 && (
        <div key={updateKey} className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h4 className="text-sm font-medium text-white">
                Recommended Artists ({recommendations.length})
              </h4>
              {recSource && (
                <p className="text-[10px] text-gray-500">
                  via {recSource === 'spotify_recommendations' ? 'Spotify' :
                       recSource === 'local_auralist' ? 'Auralist DB' :
                       recSource === 'music-recs-api/similar' ? 'Last.fm data' : recSource}
                </p>
              )}
            </div>
            <button
              onClick={playAll}
              disabled={loadingPlayAll || loadingEnrich}
              className="px-4 py-2 bg-spotify-green text-black font-semibold rounded-lg text-sm hover:bg-spotify-green/90 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {loadingPlayAll ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
                  Starting...
                </>
              ) : (
                <>&#9658; Play All</>
              )}
            </button>
          </div>

          <div className="space-y-2 max-h-[60vh] md:max-h-[500px] overflow-y-auto scrollbar-hidden">
            {recommendations.map((rec, index) => {
              const enriched = getEnrichedArtist(rec.artist);
              const hasTrack = enriched?.found && enriched?.top_track;
              return (
                <div key={`${rec.artist}-${index}`} className="flex items-center gap-3 p-3 md:p-3 bg-gray-800/50 rounded-lg hover:bg-gray-800 active:bg-gray-700 transition-colors">
                  <span className="text-xs text-gray-500 w-5 md:w-6 flex-shrink-0">{index + 1}.</span>
                  {hasTrack && enriched.top_track?.album_image ? (
                    <img src={enriched.top_track.album_image} alt="" className="w-14 h-14 md:w-12 md:h-12 rounded object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-14 h-14 md:w-12 md:h-12 rounded bg-gray-700 flex items-center justify-center text-gray-500 flex-shrink-0">
                      <span className="text-2xl md:text-xl">&#9836;</span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-base md:text-sm font-medium text-white capitalize truncate">
                      {enriched?.spotify_artist?.name || rec.artist}
                    </p>
                    {hasTrack ? (
                      <p className="text-sm md:text-xs text-gray-400 truncate">{enriched.top_track?.name}</p>
                    ) : (
                      <p className="text-xs md:text-[10px] text-gray-500">Score: {rec.score.toFixed(3)}</p>
                    )}
                  </div>
                  {hasTrack ? (
                    <button
                      onClick={() => playTrack(enriched.top_track!.uri)}
                      className="min-w-touch min-h-touch w-12 h-12 md:w-auto md:h-auto md:px-4 md:py-2 bg-spotify-green text-black rounded-full text-base md:text-sm font-semibold hover:scale-105 active:scale-95 transition-transform flex items-center justify-center"
                    >
                      &#9658;
                    </button>
                  ) : (
                    <span className="text-xs text-gray-600 px-2">{loadingEnrich ? '...' : 'No track'}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {selectedPlaylist && artistMatch && artistMatch.matched_count === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-400 text-sm">
            No artists from this playlist found in our database. Try a different playlist.
          </p>
        </div>
      )}
    </div>
  );
};

export default PlaylistRecommender;
